import i18n from '~/components/i18n'
import { StepId } from './constants'

export function getNumberOfFeeds(feeds) {
  let sum = 0
  Object.keys(feeds).map(key => {
    if (key === 'coarseFeeds' || key === 'juicyFeeds' || key === 'concentratedFeeds') {
      sum += Object.values(feeds[key]).length
    }
  })
  return sum
}

export function getNumberOfFilledFeeds(feeds) {
  let sum = 0
  Object.keys(feeds).map(key => {
    if (key === 'coarseFeeds' || key === 'juicyFeeds' || key === 'concentratedFeeds') {
      sum += Object.values(feeds[key]).filter(feed => !!feed['value']).length
    }
  })
  return sum
}

export function findJuicyFeed(feeds) {
  const feedGroupWithJuicyFeed = feeds.find(group => group.juicyFeeds && group.juicyFeeds.length > 0);

  // Return the first juicyFeed found, or null if none exists
  if (feedGroupWithJuicyFeed) {
    return feedGroupWithJuicyFeed.juicyFeeds[0];
  }

  return null;
}

export function totalJuicyFeedsSelected(feedGroups) {
  return feedGroups.reduce((previousJuicyFeedsCount, feedGroup) => {
    previousJuicyFeedsCount += feedGroup.juicyFeeds.length
    return previousJuicyFeedsCount
  }, 0)
}

export function getSumOfFilledFeeds(feeds) {
  let sum = 0
  Object.keys(feeds).map(key => {
    if (key === 'coarseFeeds' || key === 'juicyFeeds' || key === 'concentratedFeeds') {
      const feedsSum = feeds[key].reduce((previosValue, currentValue) => {
        if (currentValue?.value && Boolean(currentValue?.value)) {
          previosValue = previosValue + parseFloat(currentValue?.value?.replace(',', '.'))
          return previosValue
        }
        return previosValue
      }, 0) || 0
      sum = sum + feedsSum
    }
  })
  return sum
}

export function redistributeCattleNumbers(feedGroups, cattleNumber) {
  const currentTotal = feedGroups.reduce((sum, fg) => sum + fg.cattleNumber, 0);
  const totalCattleNumber = parseInt(cattleNumber?.toString().replace(',', '.'))

  const proportional = feedGroups.map(fg => ({
    ...fg,
    raw: (fg.cattleNumber / currentTotal) * totalCattleNumber
  }));

  const withFloor = proportional.map(fg => ({
    ...fg,
    cattleNumber: Math.floor(fg.raw)
  }));

  const remainder = totalCattleNumber - withFloor.reduce((sum, fg) => sum + fg.cattleNumber, 0);

  const sortedByFraction = [...withFloor].sort((a, b) =>
    (b.raw - b.cattleNumber) - (a.raw - a.cattleNumber)
  );

  for (let i = 0; i < remainder; i++) {
    sortedByFraction[i].cattleNumber += 1;
  }

  const final = feedGroups.map(fg =>
    sortedByFraction.find(s => s.name === fg.name)!
  );

  return final;
}

export function redistributeDays(feedGroups, calfRearingDays) {
  // Target calf days
  const targetDays = calfRearingDays;
  // Step 1: Calculate initial rounded days and remainders
  let totalRoundedDays = 0;
  const remainders = [];

  feedGroups.forEach(group => {
    const roundedDays = Math.floor(group.days); // Floor value to nearest integer
    totalRoundedDays += roundedDays; // Accumulate total rounded days
    remainders.push({ index: feedGroups.indexOf(group), remainder: group.days - roundedDays }); // Track index and remainder
    group.days = roundedDays; // Update feedGroups directly by flooring the days
  });

  // Step 2: Calculate the difference between target days and rounded total
  const daysToDistribute = targetDays - totalRoundedDays;

  // Step 3: Sort remainders in descending order to distribute missing days
  if (daysToDistribute > 0) {
    // Sort by largest remainders to distribute missing days
    remainders.sort((a, b) => b.remainder - a.remainder);
    for (let i = 0; i < daysToDistribute; i++) {
      // Update the feedGroups directly by incrementing the 'days' value
      feedGroups[remainders[i].index].days += 1;
    }
  } else if (daysToDistribute < 0) {
    // Sort by smallest remainders to subtract excess days
    remainders.sort((a, b) => a.remainder - b.remainder);
    for (let i = 0; i < Math.abs(daysToDistribute); i++) {
      // Update the feedGroups directly by decrementing the 'days' value
      feedGroups[remainders[i].index].days -= 1;
    }
  }
  return feedGroups
}

function phaseFeedingDataParse(feedingPhases) {
  feedingPhases.forEach(feedingPhase => {
    feedingPhase.coarseFeeds.forEach(feed => {
      feed.value = feed.value?.replace(',', '.');
    });
    feedingPhase.concentratedFeeds.forEach(feed => {
      feed.value = feed.value?.replace(',', '.');
    });
    feedingPhase.juicyFeeds.forEach(feed => {
      feed.value = feed.value?.replace(',', '.');
    });
  });
  return feedingPhases
}

export function prepareDataForPayload(data) {
  Object.keys(data).forEach(key => {
    if (typeof data[key] === 'string') {
      data[key] = data[key].replace(',', '.');
    } else if (key === 'feedGroups') {
      data.feedGroups.forEach(feedGroup => {
        if (feedGroup.feedingPhases?.length > 0) {
          feedGroup.feedingPhases = phaseFeedingDataParse(feedGroup.feedingPhases)
        } else {
          feedGroup?.coarseFeeds?.forEach(feed => {
            feed.value = feed.value?.replace(',', '.');
          });
          feedGroup?.concentratedFeeds?.forEach(feed => {
            feed.value = feed.value?.replace(',', '.');
          });
          feedGroup?.juicyFeeds?.forEach(feed => {
            feed.value = feed.value?.replace(',', '.');
          });
        }
      });
    } else if (key === 'rearingPhases') {
      data.rearingPhases.forEach(phase => {
        phase?.coarseFeeds?.forEach(feed => {
          feed.value = feed.value?.toString()?.replace(',', '.');
        });
        phase?.concentratedFeeds?.forEach(feed => {
          feed.value = feed.value?.toString()?.replace(',', '.');
        });
      });
    }
    else if ([
      'cowsManureFermentations',
      'calvesManureFermentations',
      'cowsManureStorageTypes',
      'calvesManureStorageTypes',
      'cowsEnergyConsumptions',
      'calvesEnergyConsumptions'
    ].includes(key)) {
      data[key].forEach(item => {
        item.percent = item.percent.replace(',', '.');
      });
    } else if (['calvesConcentratedFeeds', 'calvesCoarseFeeds', 'cowsCoarseFeeds', 'cowsConcentratedFeeds', 'juicyFeeds'].includes(key)) {
      data[key].forEach(item => {
        item.dryMatter = item.dryMatter.replace(',', '.');
        item.energy = item.energy.replace(',', '.');
        item.rawFat = item.rawFat?.replace(',', '.');
        item.rawProtein = item.rawProtein?.replace(',', '.');
        item.rawAsh = item.rawAsh?.replace(',', '.');
        item.rawFiber = item.rawFiber?.replace(',', '.');
      });
    } else if (['calvesHousingTypes', 'cowsHousingTypes'].includes(key)) {
      data[key].forEach(item => {
        item.cattleNumber = item.cattleNumber?.toString()?.replace(',', '.');
      });
    } else if (key === 'weaningWeeksValues') {
      data[key].forEach(item => {
        item.milkReplacer = item.milkReplacer?.toString()?.replace(',', '.');
        item.wholeMilk = item.wholeMilk?.toString()?.replace(',', '.');
        item.calfConcentratedFeed = item.calfConcentratedFeed?.toString()?.replace(',', '.');
      });
    } else if ((key === 'maizeSilage' || key === 'grassSilage') && data[key]) {
      Object.entries(data[key])?.forEach(([propKey, value]) => {
        if (typeof value === 'string') {
          data[key][propKey] = value.replace(',', '.');
        }

        if (Array.isArray(value)) {
          value?.forEach((item, index) => {
            if (typeof item === 'object' && item !== null) {
              Object.entries(item)?.forEach(([itemKey, itemValue]) => {
                if (typeof itemValue === 'string') {
                  data[key][propKey][index][itemKey] = itemValue.replace(',', '.');
                }
              });
            }
          });
        }
      });
    }
  });
  return data
}

export function getTransformFeedGroups(feeds) {
  const feedsTransformed = []
  feeds.map((feedGroup, index) => {
    if (feedGroup['feedingPhases'] && feedGroup['feedingPhases']?.length > 0) {
      feedGroup['feedingPhases'].map(feedPhase => {
        const feedGroupTitle = feedGroup['name']
        const title = i18n.t('{{feedTitle}} / {{phaseTitle}}', { feedTitle: feedGroupTitle, phaseTitle: feedPhase.name })
        feedsTransformed.push({ ...feedPhase, feedGroupIndex: index, name: title, type: feedGroup.type })
      })
    } else {
      feedsTransformed.push({ ...feedGroup, feedGroupIndex: index, type: feedGroup.type })
    }
  })
  return feedsTransformed
}

export function getTransformCalvesFeedGroups(feeds) {
  const feedsTransformed = []
  feeds.map((feedGroup, index) => {
    if (feedGroup.type === 3) {
      if (feedGroup['feedingPhases'] && feedGroup['feedingPhases']?.length > 0) {
        feedGroup['feedingPhases'].map(feedPhase => {
          const feedGroupTitle = feedGroup['name']
          const title = i18n.t('{{feedTitle}} / {{phaseTitle}}', { feedTitle: feedGroupTitle, phaseTitle: feedPhase.name })
          feedsTransformed.push({ ...feedPhase, feedGroupIndex: index, name: title, type: feedGroup.type })
        })
      } else {
        feedsTransformed.push({ ...feedGroup, feedGroupIndex: index, type: feedGroup.type })
      }
    }
  })
  return feedsTransformed
}

export function getTransformCowsFeedGroups(feeds) {
  const feedsTransformed = []
  feeds.map((feedGroup, index) => {
    if (feedGroup.type !== 3) {
      if (feedGroup['feedingPhases'] && feedGroup['feedingPhases']?.length > 0) {
        feedGroup['feedingPhases'].map(feedPhase => {
          const feedGroupTitle = feedGroup['name']
          const title = i18n.t('{{feedTitle}} / {{phaseTitle}}', { feedTitle: feedGroupTitle, phaseTitle: feedPhase.name })
          feedsTransformed.push({ ...feedPhase, feedGroupIndex: index, name: title, type: feedGroup.type })
        })
      } else {
        feedsTransformed.push({ ...feedGroup, feedGroupIndex: index, type: feedGroup.type })
      }
    }
  })
  return feedsTransformed
}

export function getStepHeadMessage(stepId: StepId): string {
  switch (stepId) {
    case StepId.DAIRY_DATA:
      return 'Hohe Milchleistungen können einen positiven Einfluss auf den CO<sub>2</sub>-Fußabdruck haben, da die Emissionen für den Erhaltungsbedarf des Tieres auf eine größere Milchmenge verteilt wird.'
    case StepId.DAIRY_COW_METRICS:
      return `Für den Erhaltungsbedarf der Tiere entstehen Emissionen. Eine gute Tiergesundheit, 
      lange Nutzungsdauern und geringe Abgangszahlen haben einen positiven Einfluss auf den CO<sub>2</sub>-Fußabdruck.`
    case StepId.HEIFER_METRICS:
      return `Bitte machen Sie Angaben für alle Tiere vom 29. Lebenstag (LT) bis zur Abkalbung. 
      Die Bestandsergänzung verursacht Treibhausgasemissionen und wird der Milch- bzw. Fleischproduktion angerechnet. 
      Je niedriger das Erstkalbealter und die Verluste, desto niederiger ist der CO<sub>2</sub>-Fußabdruck.”`
    case StepId.CALF_REARING:
      return `Die Kälberaufzucht verursacht Treibhausgasemissionen und wird der Milch- bzw. Fleischproduktion angerechnet. 
      Je effizienter die Tränke und die Zunahmen und je niedriger die Verluste, desto niederiger ist der CO<sub>2</sub>-Fußabdruck. `
    case StepId.DAIRY_COW_HUSBANDRY:
    case StepId.HEIFER_HUSBANDRY:
      return `Wie viele Ihrer Tiere standen in der jeweiligen Aufstallungsform? Bitte berücksichtigen Sie auch Trockensteher und 
      Vorbereiter, Abkalbebox, Krankenbox, weitere. Bitte geben Sie hier die gesamte Anzahl an Tieren an,
       die während der Stallsaison im Stall standen. Die ggf. auf der Weide verbrachte Zeit rechnet das System automatisch gegen.`
    case StepId.DAIRY_COW_MANURE:
    case StepId.HEIFER_MANURE:
      return `<div>Bitte machen Sie Angaben für alle Tiere vom 29. Lebenstag (LT) bis zur Abkalbung. Die Bestandsergänzung verursacht 
      Treibhausgasemissionen und wird der Milch- bzw. Fleischproduktion angerechnet. Je niedriger das Erstkalbealter und die Verluste, desto niedriger
       ist der CO<suv>2</sub>-Fußabdruck.</div>`
    case StepId.DAIRY_COW_ENERGY:    
      return `<div>Je niedriger der Verbrauch von fossilen Energieträgern, desto niedriger sind die Treibhaugasemissionen.</div> 
      <div><strong>Bitte machen Sie nur Angaben für die Milchkuhherde(n), die Färsen werden später erfasst.</strong></div>`
    case StepId.HEIFER_ENERGY:
       return `<div>Je niedriger der Verbrauch von fossilen Energieträgern, desto niedriger sind die Treibhaugasemissionen.</div> 
      <div><strong>Bitte machen Sie nur Angaben für die Färsen.</strong></div>`
    case StepId.PERFORMANCE_GROUP:
      return '<strong>Achtung</strong>: Bitte geben Sie nicht die Infos zur aktuellen Fütterung, sondern zum berücksichtigten Betrachtungszeitraum an!'
    case StepId.DAIRY_COW_FEED:
    case StepId.HEIFER_FEED:
      return `Bitte geben Sie nicht die Infos zur aktuellen Fütterung, sondern zum berücksichtigten Betrachtungszeitraum an! 
      Die Optimierung der Grundfutterqualität hat einen positiven Einfluss auf den CO<sub>2</sub>-Fußabdruck, denn es ist weniger Kraftfutter nötig, um dieselbe Milchleistung zu erbringen.  
      Kraftfuttererzeugung verursacht in der Regel höhere Emissionen als die Grundfuttererzeugung. `
    case StepId.ADDITIVES_USAGE:
      return 'Diese Datenerfassung wirkt sich vorerst nicht auf Ihre Klimabilanz aus.'
    case StepId.LAND_USE:
      return `Diese Datenerfassung wirkt sich vorerst nicht auf Ihre Klimabilanz aus. Die Auswirkungen der Bewirtschaftung organischer Böden auf den CO<sub>2</sub>-Fußabdruck ist sehr stark, 
      derzeit fehlt aber eine ausreichende Datengrundlage und eine valide Zuordnung der Emissionen zu Produkten für eine eindeutige Ausweisung der Emissionen.`
    case StepId.GHG_RESULT:
      return `Die Produkt CO<sub>2</sub>-Fußabdrücke werden mit der Allokation gemäß der International Dairy Federation (IDF) in CO<sub>2</sub>-Äquivalenten je kg verkaufter Milch (FPCM) bzw. 
      je kg Lebendgewicht Schlachtkuh- und Kälberverkauf bzw. je erzeugte Färse und Jahr ausgewiesen. 
      Die IDF-Werte sind international vergleichbar und anerkannt. Die gesamten THG-Emissionen stellen alle auf dem Betrieb angefallenen Emissionen vor der Zuteilung auf die Produkte dar.`
    case StepId.FORAGE_FEEDS:
      return ` Die Abfrage zum Eigenanbau von Futtermitteln dient dazu, die entstehenden Emissionen den richtigen Verantwortungsbereichen (Scopes) zuzuordnen. 
      Emissionen aus der Herstellung eigener Futtermittel auf Ihrem Betrieb gehören zu Scope 1. Sie haben hier die volle Kontrolle über die Ursachen und können direkt Einfluss nehmen. Emissionen aus zugekauften Futtermitteln fallen in Scope 3. 
      Diese können Sie nur indirekt, zum Beispiel über Ihre Lieferantenwahl, beeinflussen.`
    default:
      return ''
  }
}

export const heiferFeedingPhases = (firstCalvingAge: number) => [
  {
    name: 'Jungtier 1',
    days: 63,
    fixed: true,
    order: 1,
    period: '0-3',
    phase: 1,
    concentratedFeeds: [],
    coarseFeeds: []
  },
  {
    name: 'Jungtier 2',
    days: 91,
    fixed: false,
    order: 2,
    period: '3-6',
    phase: 2,
    concentratedFeeds: [],
    coarseFeeds: []
  },
  {
    name: 'Jungtier 3',
    days: 182,
    fixed: false,
    order: 3,
    period: '7 - 12',
    phase: 2,
    concentratedFeeds: [],
    coarseFeeds: []
  },
  {
    name: 'Jungtier 4',
    days: 365,
    fixed: false,
    order: 4,
    period: '13-24',
    phase: 2,
    concentratedFeeds: [],
    coarseFeeds: []
  },
  {
    name: 'Jungtier 5',
    days: Math.round((firstCalvingAge * 30.5 - 730.5) * 10) / 10,
    fixed: false,
    order: 5,
    period: '24-EKA',
    phase: 2,
    concentratedFeeds: [],
    coarseFeeds: []
  }
];

export const individualPhases = (firstCalvingAge) => [
  {
    name: '1. Aufzuchtphase',
    days: 63,
    fixed: true,
    order: 1,
    period: '0-3 Monate',
    phase: 1,
    concentratedFeeds: [],
    coarseFeeds: []
  },
  {
    name: '2. Aufzuchtphase',
    days: Math.round(firstCalvingAge * 30.5 - 63 - 28),
    fixed: true,
    order: 2,
    period: '3-EKA Monate',
    phase: 2,
    concentratedFeeds: [],
    coarseFeeds: []
  }
]

export const BROWN_COW_ID = 3
export const BLACK_WHITE_COW_ID = 4
export const JERSEY_COW_ID = 5
export const DRY_PERIOD_TYPE = 1
export const LACTATING_PERIOD_TYPE = 2
